import React, { FC, useEffect, useState } from 'react'
import { ButtonOLD, MultiTextArea, PageHeader } from '@lmc/ui-kit'
import { Row, Form } from 'antd'
import { history } from '../../../../../store'
import { useTranslation } from 'react-i18next'
import { withRouter } from '../../../../../withRouter'
import { AboutMe, Carer, Resident } from '@lmc/ts-common'
import { Spinner } from '../../../../../components/generic'
import { getTitle } from '../utils'
import { isBrowser, isTablet } from 'react-device-detect'
import PdfExport from '../../../../../components/pdf/PdfExport'
import PDFHeader from '../../ResidentConsentScreens/ConsentFormScreen/PDFScreen/PDFHeader'
import { RouterProps } from '@lmc/common/types'
import { showToast } from '../../../../../common/utils'
import styled from 'styled-components'
import { ProTag } from 'components/ProTag'
import NavigationModal from 'components/NavigationModal/NavigationModal'

interface AboutMeSectionProps {
  aboutMe: AboutMe
  carers: Carer[]
  saveForm?: (form: any, id: string) => Promise<any>
  fetchAboutMe?: (id: string) => Promise<any>
  router: RouterProps
  requestAboutMe?: boolean
  printPDF?: boolean
  resident: Resident
  containerRef?: any
  onHistoryBack: () => void
}

export enum SectionType {
  importantAboutMe = 'importantAboutMe',
  importantPeople = 'importantPeople',
  communicationPreferences = 'communicationPreferences',
  myWellness = 'myWellness',
  doAndDont = 'doAndDont',
  howAndWhen = 'howAndWhen',
  worthKnowing = 'worthKnowing',
}

const isDesktop = isBrowser || isTablet

export const AboutMeSection: FC<AboutMeSectionProps> = ({
  saveForm,
  fetchAboutMe,
  aboutMe,
  carers,
  router,
  requestAboutMe = true,
  printPDF,
  resident,
  containerRef,
  onHistoryBack,
}) => {
  const { t } = useTranslation()

  const [form] = Form.useForm()

  const residentId = router.params.id

  const [editMode, setEditMode] = React.useState(false)

  const [loading, setLoading] = React.useState(true)

  const [formChange, setFormChange] = useState(aboutMe)

  const styles = {
    footer: {
      justifyContent: 'flex-end',
      width: isBrowser || isTablet ? 'calc(100vw - 290px)' : '100vw',
    },
    logoIcon: {
      width: 24,
      height: 24,
    },
    historyButton: { marginRight: 8 },
    pdfWrapper: {
      position: 'absolute' as any,
      top: 0,
      left: 0,
      width: 1190,
      minHeight: 1480,
      zIndex: 1000,
      background: 'white',
      padding: '24px 28px',
    },
  }

  const scrollToTop = () => {
    if (containerRef) containerRef.current.scrollIntoView()
    window.scrollTo(0, 0)
  }

  useEffect(() => {
    if (requestAboutMe) {
      fetchAboutMe?.(residentId).then(() => {
        setLoading(false)
      })
      handleCloseForm()
    }
    scrollToTop()
  }, [residentId])

  const onFinish = async (values: AboutMe) => {
    const tempValues = { ...values }
    Object.values(SectionType).forEach((type) => {
      if (!form.isFieldTouched(type)) {
        delete tempValues[type]
        delete tempValues[`${type}WrittenBy`]
      }
    })
    setLoading(true)
    try {
      const rest = await saveForm?.({ ...tempValues }, residentId)
      if (rest) {
        setLoading(false)
        showToast({ title: t('general.forms.successfullySaved') })
        handleCloseForm()
      }
    } catch (e) {
      showToast({
        type: 'error',
        title: t('general.forms.internalError'),
      })
    }
  }

  const handleOpenForm = () => {
    form.setFieldsValue(aboutMe)
    setEditMode(true)
  }

  const handleSubmit = async () => {
    await form.submit()
  }

  const onFinishFailed = (error: any) => {
    let title = t('general.forms.missingFieldsWarning')
    if (error && error.errorFields) {
      title = error?.errorFields[0]?.errors[0]
    }
    showToast({ type: 'warning', title })
  }

  const handleCloseForm = () => {
    setEditMode(false)
    setFormChange({} as AboutMe)
    form.resetFields()
  }

  const aboutMeSections = [
    {
      source: SectionType.importantAboutMe,
      label: t(`aboutMe.sections.importantAboutMe`),
      tooltip: t(`aboutMe.sections.importantAboutMeTooltip`),
    },
    {
      source: SectionType.importantPeople,
      label: t(`aboutMe.sections.importantPeople`),
      tooltip: t(`aboutMe.sections.importantPeopleTooltip`),
    },
    {
      source: SectionType.communicationPreferences,
      label: t(`aboutMe.sections.communicationPreferences`),
      tooltip: t(`aboutMe.sections.communicationPreferencesTooltip`),
    },
    {
      source: SectionType.myWellness,
      label: t(`aboutMe.sections.myWellness`),
      tooltip: t(`aboutMe.sections.myWellnessTooltip`),
    },
    {
      source: SectionType.doAndDont,
      label: t(`aboutMe.sections.doAndDont`),
      tooltip: t(`aboutMe.sections.doAndDontTooltip`),
    },
    {
      source: SectionType.howAndWhen,
      label: t(`aboutMe.sections.howAndWhen`),
      tooltip: t(`aboutMe.sections.howAndWhenTooltip`),
    },
    {
      source: SectionType.worthKnowing,
      label: t(`aboutMe.sections.worthKnowing`),
      tooltip: t(`aboutMe.sections.worthKnowingTooltip`),
    },
  ]

  const pdfUrl = `residents/${residentId}/pdf/about-me`

  const url = `/api/services/pdf/export?url=${encodeURIComponent(pdfUrl)}`

  const checkFormData = aboutMe?.resident
    ? aboutMe?.resident !== residentId
    : false

  if (checkFormData) {
    return <Spinner />
  }

  const getPDFResidentName = () =>
    `${resident?.name?.first} ${resident?.name?.last} - ${t(`aboutMe.title`)}`

  const getTitleWithResidentName = () =>
    `${t(`aboutMe.titleAbout`)} ${resident?.name?.first} ${
      resident?.name?.last
    }`

  return (
    <div style={printPDF ? styles.pdfWrapper : {}}>
      <NavigationModal canNavigate={!(editMode && form.isFieldsTouched())} />
      <Row>
        {printPDF && (
          <PDFHeader
            resident={resident as any}
            headingExtra={` - ${t(`aboutMe.title`)}`}
          />
        )}
      </Row>
      {!printPDF && (
        <PageHeader
          sticky
          title={getTitleWithResidentName()}
          subtitle={getTitle(
            aboutMe?.editedBy as string,
            // @ts-ignore
            aboutMe?.editedOn,
            t(`aboutMe.reviewed`),
            carers
          )}
          onBack={onHistoryBack ? () => onHistoryBack?.() : undefined}
        >
          <ProTag />
          {requestAboutMe && (
            <SpaceBetweenWrapper>
              <FlexWrapper>
                <div>
                  <ButtonOLD
                    style={styles.historyButton}
                    type="secondary"
                    onClick={() =>
                      history.push(`/residents/${residentId}/about-me/history`)
                    }
                  >
                    {t(`aboutMe.history`)}
                  </ButtonOLD>
                </div>
                <div>
                  <PdfExport
                    pdfTitle={getPDFResidentName()}
                    url={url}
                    text={isDesktop ? t(`general.buttons.exportPDF`) : 'PDF'}
                  />
                </div>
              </FlexWrapper>
            </SpaceBetweenWrapper>
          )}
        </PageHeader>
      )}
      <Form
        form={form}
        onFinish={onFinish}
        layout="vertical"
        scrollToFirstError
        onFinishFailed={onFinishFailed}
        onChange={(e: any) =>
          setFormChange({
            ...formChange,
            [e.target.id]: e.target.value,
          })
        }
      >
        {aboutMeSections.map((section) => {
          const localisation = {
            description: t(`aboutMe.description`),
            writtenBy: t(`aboutMe.writtenBy`),
          }
          return (
            <MultiTextArea
              formChange={formChange}
              printPDF={printPDF}
              key={section.source}
              heading={section.label}
              headingInfo={getTitle(
                aboutMe?.[`${section.source}EditedBy`] as string,
                // @ts-ignore
                aboutMe?.[`${section.source}EditedOn`],
                t(`aboutMe.reviewed`),
                carers
              )}
              tooltip={section.tooltip}
              fields={[
                {
                  source: section.source,
                  // @ts-ignore
                  value: aboutMe?.[section.source],
                  element: 'textarea',
                  editMode,
                  label: section.label,
                  localisation,
                },
                {
                  source: `${section.source}WrittenBy`,
                  // @ts-ignore
                  value: aboutMe?.[`${section.source}WrittenBy`],
                  element: 'input',
                  editMode,
                  label: section.label,
                  localisation,
                },
              ]}
              printPDFPageBreak={false}
            />
          )
        })}
      </Form>
      {requestAboutMe && !printPDF && (
        <div
          className="lmc-fixed-footer"
          style={{
            ...styles.footer,
          }}
        >
          {editMode ? (
            <>
              <ButtonOLD onClick={handleCloseForm} type="secondary">
                {t(`general.buttons.cancel`)}
              </ButtonOLD>
              <ButtonOLD
                onClick={handleSubmit}
                type="primary"
                style={{ marginLeft: 8 }}
                loading={loading}
              >
                {t(`general.buttons.save`)}
              </ButtonOLD>
            </>
          ) : (
            <ButtonOLD onClick={handleOpenForm} type="primary">
              {t(`general.buttons.edit`)}
            </ButtonOLD>
          )}
        </div>
      )}
    </div>
  )
}

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: row;
`

const SpaceBetweenWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`

export default withRouter(AboutMeSection)
